import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ArrowLeft, Wallet, TrendingUp, CalendarDays, Banknote } from 'lucide-react';
import SEO from '../components/SEO';
import { getTrainerEarnings } from '../lib/api';
import { formatCurrency } from '../lib/utils';

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#C0A062]" />
      <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const Earnings = () => {
  const navigate = useNavigate();
  const { isTrainer, isAuthenticated, loading } = useAuth();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !isTrainer) return;
    (async () => {
      try {
        const res = await getTrainerEarnings();
        setData(res.data);
      } catch { /* silent */ }
      finally { setFetching(false); }
    })();
  }, [isAuthenticated, isTrainer]);

  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isTrainer) return <Navigate to="/" replace />;

  const rows = data?.sessions || [];

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="earnings-page">
      <SEO title="Earnings | DojoDash Coach" description="Track your session payouts and balance on DojoDash." />
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-zinc-400 hover:text-white"
          data-testid="earnings-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to dashboard
        </Button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Wallet className="w-6 h-6 text-[#C0A062]" />
              <h1 className="text-3xl font-bold text-white">Earnings</h1>
            </div>
            <p className="text-zinc-400">Payouts, balance, and recent activity for your coaching sessions.</p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} data-testid="request-payout-wrap">
                  <Button
                    disabled
                    className="bg-[#C0A062] text-black font-semibold pointer-events-none opacity-60"
                    data-testid="request-payout-btn"
                  >
                    <Banknote className="w-4 h-4 mr-2" />
                    Request Payout
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="bg-[#18181B] border-zinc-700 text-zinc-200" data-testid="request-payout-tooltip">
                Payouts coming soon — currently in test mode
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {fetching ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <StatCard icon={TrendingUp} label="Total earnings" value={formatCurrency(data?.total_earnings || 0)} testId="stat-total-earnings" />
              <StatCard icon={CalendarDays} label="This month" value={formatCurrency(data?.month_earnings || 0)} testId="stat-month-earnings" />
              <StatCard icon={Wallet} label="Paid sessions" value={data?.session_count || 0} testId="stat-session-count" />
            </div>

            {/* Per-session breakdown */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden" data-testid="earnings-table-card">
              <div className="px-5 py-4 border-b border-zinc-800">
                <h2 className="text-white font-semibold">Session breakdown</h2>
              </div>
              {rows.length === 0 ? (
                <div className="p-10 text-center" data-testid="earnings-empty-state">
                  <p className="text-white font-semibold mb-1">No paid sessions yet</p>
                  <p className="text-zinc-400 text-sm">Once clients book and pay for sessions, your earnings will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="earnings-table">
                    <thead>
                      <tr className="text-left text-zinc-500 text-xs uppercase tracking-wider border-b border-zinc-800">
                        <th className="px-5 py-3 font-semibold">Date</th>
                        <th className="px-5 py-3 font-semibold">Client</th>
                        <th className="px-5 py-3 font-semibold">Duration</th>
                        <th className="px-5 py-3 font-semibold text-right">Amount</th>
                        <th className="px-5 py-3 font-semibold text-right">Platform fee</th>
                        <th className="px-5 py-3 font-semibold text-right">Net payout</th>
                        <th className="px-5 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((s) => (
                        <tr key={s.id} className="border-b border-zinc-800/60 last:border-0 hover:bg-zinc-800/20" data-testid={`earnings-row-${s.id}`}>
                          <td className="px-5 py-3 text-zinc-300 whitespace-nowrap">{s.date}{s.time ? ` · ${s.time}` : ''}</td>
                          <td className="px-5 py-3 text-white">{s.client_name}</td>
                          <td className="px-5 py-3 text-zinc-400">{s.duration_minutes} min</td>
                          <td className="px-5 py-3 text-zinc-300 text-right">{formatCurrency(s.amount)}</td>
                          <td className="px-5 py-3 text-zinc-500 text-right">-{formatCurrency(s.platform_fee)}</td>
                          <td className="px-5 py-3 text-[#C0A062] font-semibold text-right">{formatCurrency(s.net_payout)}</td>
                          <td className="px-5 py-3">
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${
                                s.payout_status === 'paid'
                                  ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                                  : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                              }`}
                              data-testid={`payout-status-${s.id}`}
                            >
                              {s.payout_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Earnings;
